import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { WaiversPoliciesApi } from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";

export const usePolicyBulkDelete = (
  toast: ReturnType<typeof import("@/components/feedback").useToast>,
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: async (ids: number[]) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(WaiversPoliciesApi, accessToken);
      await Promise.all(
        ids.map((id) => service.apiPoliciesIdDelete({ id })),
      );
    },
    onSuccess: (_, ids) => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.Waivers] });
      const count = ids.length;
      toast({
        title: count === 1 ? "Policy deleted" : "Policies deleted",
        description:
          count === 1
            ? "Policy deleted successfully."
            : `${count} policies deleted successfully.`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error ? error.message : "Failed to delete policies";
      toast({
        title: "Error",
        description: message,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, isPending };
};
